import { useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"
import _ from "lodash"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useTranslation } from "providers/i18n"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-service-and-tourist/constant"
import { getIcon } from "utils/icon"

import { VenueSelector } from "components/VenueSelector"
import { PageCard, PageCardContent } from "components/card"
import { MotionDiv } from "components/motions"
import { AppNavigation } from "containers/AppNavigation"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { EmptyData } from "components/EmptyData"
import { ShopIcon } from "components/icons"
import { useVenueController } from "hooks/venue/useVenueController"
import { useServiceAndTourist } from "extensions/em-service-and-tourist/hooks/useServiceAndTourist"
import { OccupantImageCard } from "extensions/em-core/components/ImageCard"

export const EmServiceAndTouristKioskHPage = ({ Layout = Box }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { isMultiVenueProject, isMultiOrdinalVenue, venues } = useVenue()
  const { locationVenue } = useGeoLocation()
  const { extensionConfig } = useExtension()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const theme = useTheme()

  const serviceAndTouristConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        locations: [],
      },
    [extensionConfig]
  )
  const serviceLocations = _.get(serviceAndTouristConfig, "locations")

  const { filterByVenueId, filteredServiceAndTouristByVenue } =
    useServiceAndTourist(serviceLocations, venueFilter)

  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ShopIcon />,
    [theme]
  )

  const styleOverrides =
    theme.components[`EmServiceAndTouristPage`]?.styleOverrides || {}
  const {
    pageCard,
    contentWrapper,
    gridContainer,
    serviceItem,
    emptyDataWrapper,
    venueSelector,
  } = styleOverrides

  return (
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={bannerRoot}></Box>
            <Box sx={body}>
              <AppNavigation />
            </Box>
            <Box sx={footer}>
              <NavigationControl BackButtonProps={{ hidden: true }} />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            {filteredServiceAndTouristByVenue?.length > 0 ? (
              <Box sx={gridContainer}>
                {filteredServiceAndTouristByVenue?.map((data) => {
                  const { id, name, image, level_name, location } = data || {}

                  const serviceName = t(name)
                  const levelText = isMultiOrdinalVenue
                    ? t("Level Text", { level: t(level_name) })
                    : ""

                  return (
                    <OccupantImageCard
                      sx={serviceItem}
                      key={id}
                      id={id}
                      coverImage={image}
                      primaryText={serviceName}
                      adornmentText={levelText}
                      onClick={() => navigate(`/maps/place/${location.id}`)}
                    />
                  )
                })}
              </Box>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
